// AI views: AIImportView, ValidationView, ExtractionDashView,
// RedlineReview, DraftingView.
//
// AIImportView is the bulk PDF→contract import flow with batch
// processing, AI field extraction, parent matching, and dedup checks.
// ValidationView is the human review queue for AI-extracted fields.
// ExtractionDashView shows extraction stats and quality metrics.
// RedlineReview displays AI-suggested redlines from playbook analysis.
// DraftingView is the contract drafting assistant with chat + tools.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { diffWords } from "diff";
import { T } from "../theme";
import { fmtDate, toTitleCase } from "../utils";
import { apiCall } from "../apiClient";
import { AI_UNIVERSAL_FIELDS } from "../constants";
import { renderMarkdown } from "./markdown";
import { PdfHighlightModal } from "./PdfHighlightModal";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { useClientScope } from "../contexts/ClientScopeContext";

// EXHIBIT_UI_W3_20260415 — exhibit template detection banner in batch import review

// Validate an extraction row before allowing import. Blocks approval when:
// - A field has a value with low confidence (AI extracted something but is uncertain)
// - A field value contains placeholder markers ([brackets], "insert here",
//   "TBD", "to be determined", etc.) — the AI hallucinated a placeholder
// - A field is empty but the AI is confident it SHOULD be present (rare)
//
// Does NOT block on: empty field + low confidence. That's the AI saying the
// field is not applicable / not in this document — a normal case (e.g., an MSA
// doesn't have "Amendment Effective Date" or "Sub-processor Requirements").
// Returns { ok, lowConf: string[], missing: string[], placeholder: string[] }.
const PLACEHOLDER_RE = /[\[\]<>{}]|insert\s|tbd|to\s+be\s+determined|placeholder|\bn\/?a\b/i;

// N/A-shaped low-confidence values. When a field is low-confidence AND its
// value matches one of these shapes, the AI is effectively saying the field
// doesn't exist in the document (common for unsigned contracts / template
// forms producing signature-date fields as "Date: ___" or "Not determinable
// from document"). Per operator design 2026-04-16, these are treated as
// empty rather than as low-confidence blockers, preserving the existing
// 2026-04-14 relaxation pattern (empty + low-conf = N/A).
const NA_PATTERNS: RegExp[] = [
  /not determinable/i,
  /cannot be (extracted|determined|found|identified)/i,
  /unable to (extract|determine|locate|find)/i,
  /^not applicable$/i,
  /no (date|value|information|term|field).{0,30}(found|present|specified|provided)/i,
  /^[\s_]+$/,
  /^\[[^\]]*\]$/,
];
const isNaEquivalent = (value: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (NA_PATTERNS.some(p => p.test(trimmed))) return true;
  // Majority-punctuation/whitespace check: captures surface forms like
  // "Date: ___________________________" where alphanumerics are sparse
  // amid fill-in punctuation.
  if (trimmed.length >= 10) {
    const nonAlphaNum = trimmed.replace(/[a-zA-Z0-9]/g, '');
    if (nonAlphaNum.length / trimmed.length > 0.6) return true;
  }
  return false;
};

const validateImportRow = (row: any): { ok: boolean; lowConf: string[]; missing: string[]; placeholder: string[]; blocking: string[] } => {
  const lowConf: string[] = [];
  const missing: string[] = [];
  const placeholder: string[] = [];
  Object.entries(row).forEach(([k, v]: [string, any]) => {
    if (k.startsWith('__') || k === 'suggestedRelationship') return;
    if (!v || typeof v !== 'object') return;
    const val = (v.v == null ? '' : String(v.v)).trim();
    const conf = (v.conf || v.confidence || 'medium').toLowerCase();
    if (!val) { if (conf !== 'low') missing.push(k); return; }
    // N/A-shaped low-confidence values behave like empty + low-conf: skip.
    // Must run BEFORE PLACEHOLDER_RE so bracketed-only low-conf values
    // (e.g. "[DATE]") are classified as N/A rather than placeholder.
    if (conf === 'low' && isNaEquivalent(val)) return;
    if (PLACEHOLDER_RE.test(val)) { placeholder.push(k); return; }
    if (conf === 'low') { lowConf.push(k); }
  });
  const blocking = [...missing, ...placeholder, ...lowConf];
  return { ok: blocking.length === 0, lowConf, missing, placeholder, blocking };
};


export const AIImportView = ({ onRefresh = undefined, clients = [] }: { onRefresh?: () => void; clients?: any[] }) => {
  const [files, setFiles] = useState<any[]>([]);
  const [realFiles, setRealFiles] = useState<any[]>([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [customFields, setCustomFields] = useState<any[]>([]);
  const [newField, setNewField] = useState("");
  const [profiles, setProfiles] = useState<any[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string>("__auto__");
  const [drag, setDrag] = useState(false);
  const [dupeWarnings, setDupeWarnings] = useState<any[]>([]);
  const [error, setError] = useState("");
  // Deployment edition — in-house tenants have a single implicit client
  // entity, so the "Client Entity (optional)" picker is suppressed. The
  // selectedClientId + clientEntity state stay around because batch resume
  // and the "Swap Parties" path both still touch them.
  const { edition } = useClientScope();
  const [clientEntity, setClientEntity] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [newClientName, setNewClientName] = useState("");
  const [existingContracts, setExistingContracts] = useState<any[]>([]);
  const [existingTree, setExistingTree] = useState<any[]>([]);
  const [parentSearch, setParentSearch] = useState<Record<string, any>>({});
  const [importedCount, setImportedCount] = useState(0);
  const [contractTypesByCategory, setContractTypesByCategory] = useState<Record<string, string[]>>({});
  const [allContractTypeNames, setAllContractTypeNames] = useState<string[]>([]);
  const [pdfHighlight, setPdfHighlight] = useState<any>(null);
  const [approvingId, setApprovingId] = useState<any>(null);
  const [retryingId, setRetryingId] = useState<any>(null);
  const [extractingExhibit, setExtractingExhibit] = useState<string | null>(null); // `${rowId}:${exhibitIndex}` when extraction in flight
  const [exhibitErrors, setExhibitErrors] = useState<Record<string, string>>({});
  // Extract-All serial runner state
  const [extractAllRunning, setExtractAllRunning] = useState<string | null>(null); // rowId currently being processed, else null
  const [extractAllProgress, setExtractAllProgress] = useState<{ current: number; total: number; currentLabel: string } | null>(null);
  const [templateWizard, setTemplateWizard] = useState<any>(null); // open = wizard data, null = closed
  const [replaceConfirm, setReplaceConfirm] = useState<any>(null);
  const wizardResolveRef = useRef<((v?: any) => void) | null>(null);
  const replaceResolveRef = useRef<((v: 'replace' | 'skip') => void) | null>(null);
  const [scanningTemplateId, setScanningTemplateId] = useState<string | null>(null);
  const [batchId, setBatchId] = useState<any>(null);
  const [batchJobs, setBatchJobs] = useState<any[]>([]);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchCompleted, setBatchCompleted] = useState(0);
  const [showNavSafe, setShowNavSafe] = useState(false);
  const [showResumeBanner, setShowResumeBanner] = useState(false);
  const pollRef = useRef<any>(null);
  const locallyActedIdsRef = useRef(new Set());

  const matchByFilename = (contracts: any[], targetFilename: string) => {
    if (!targetFilename) return null;
    const target = (targetFilename.toLowerCase().split("/").pop() || "").split("\\").pop() || "";
    return contracts.find((c: ContractRow) => {
      const cf = (c.file_name || c.file || "").toLowerCase().split("/").pop()!.split("\\").pop();
      return cf && target && cf === target;
    });
  };

  useEffect(() => { (async () => { const data = await apiCall("/contracts"); if (data?.flat) setExistingContracts(data.flat); if (data?.contracts) setExistingTree(data.contracts); })(); }, []);
  useEffect(() => { (async () => { const data = await apiCall("/extraction/extraction-profiles"); if (Array.isArray(data?.profiles)) setProfiles(data.profiles); else if (Array.isArray(data)) setProfiles(data); })(); }, []);
  useEffect(() => { (async () => { const data = await apiCall("/settings/picklists/contract-types"); if (data?.byCategory) setContractTypesByCategory(data.byCategory); if (Array.isArray(data?.types)) setAllContractTypeNames(data.types); })(); }, []);

  // On mount, check for in-progress batches to resume
  useEffect(() => {
    (async () => {
      try {
        const data = await apiCall("/extraction/batches");
        if (data?.batches) {
          const active = data.batches.find((b: any) => b.status === 'processing');
          const recentCompleted = !active ? data.batches.find((b: any) => b.status === 'completed' || b.status === 'failed') : null;
          const resumeBatch = active || recentCompleted;
          if (resumeBatch) {
            setBatchId(resumeBatch.id);
            setClientEntity(resumeBatch.client_entity || "");
            setCustomFields(resumeBatch.custom_fields || []);
            setBatchTotal(resumeBatch.total_count || 0);
            setShowResumeBanner(true);
            if (active) { setProcessing(true); setShowNavSafe(true); }
          }
        }
      } catch (err: any) { console.error("Check batches:", err); }
    })();
  }, []);

  // Poll batch status when batchId is set
  useEffect(() => {
    if (!batchId) return;
    const poll = async () => {
      try {
        const data = await apiCall(`/extraction/batch/${batchId}`);
        if (!data) return;
        const { batch, jobs } = data;
        setBatchJobs(jobs || []);
        setBatchTotal(batch.total_count || 0);
        setBatchCompleted((batch.completed_count || 0) + (batch.failed_count || 0));
        const completed = batch.completed_count + batch.failed_count;
        const total = batch.total_count || 1;
        setProgress(Math.round((completed / total) * 100));
        const processingJob = (jobs || []).find((j: any) => j.status === 'processing');
        setCurrentFile(processingJob ? processingJob.file_name : "");

        // Build results from completed jobs
        const completedJobs = (jobs || []).filter((j: any) => j.status === 'completed');
        const newResults = completedJobs.map((job: any) => {
          const extraction = job.extraction_result || {};
          const fields = extraction.fields || {};
          const row: Record<string, any> = { __file: job.file_name, __id: job.id, __jobId: job.id, __batchId: batchId, suggestedRelationship: null, __agreementCategory: extraction.agreement_category || null, __detectedExhibits: Array.isArray(job.detected_exhibits) ? job.detected_exhibits : [], __detectedProfileId: job.detected_profile_id || null, __approvedContractId: null };
          const sr = extraction.suggested_relationship;
          if (sr && typeof sr === "object" && sr.parent_id) {
            row.__parentId = sr.parent_id;
            row.__relType = sr.relationship_type || "related";
            row.__matchConfidence = sr.confidence || "medium";
            row.__matchReasoning = sr.reasoning || "";
            row.__matchParentName = sr.parent_name || "";
          } else if (sr && typeof sr === "string" && sr !== "null") {
            row.suggestedRelationship = sr;
          }
          const titleCaseFields = ['company legal name', 'counterparty legal name'];
          for (const [fieldName, fieldData] of Object.entries(fields)) {
            if (!fieldData) continue;
            const shouldTitleCase = titleCaseFields.includes(fieldName.toLowerCase());
            if (typeof fieldData === "string") {
              row[fieldName] = { v: shouldTitleCase ? toTitleCase(fieldData) : fieldData, conf: "medium" };
            } else if (typeof fieldData === "object") {
              const rawVal = String((fieldData as any).value || (fieldData as any).v || "");
              row[fieldName] = { v: shouldTitleCase ? toTitleCase(rawVal) : rawVal, conf: (fieldData as any).confidence || (fieldData as any).conf || "medium", src: (fieldData as any).source_text || null, srcSec: (fieldData as any).source_section || null };
            }
          }
          return row;
        });

        // Add failed jobs as error results
        const failedJobs = (jobs || []).filter((j: any) => j.status === 'failed');
        const failedResults = failedJobs.map((job: any) => ({
          __file: job.file_name, __id: job.id, __jobId: job.id, __batchId: batchId, __error: job.error_message || "Extraction failed", __retryable: job.retryable || false
        }));

        // Auto-resolve batch: parent references when parent has been approved
        const approvedJobs = (jobs || []).filter((j: any) => j.status === 'approved');
        for (const nr of newResults) {
          if (nr.__parentId && String(nr.__parentId).startsWith('batch:')) {
            const batchJobId = String(nr.__parentId).replace('batch:', '');
            const parentJob = approvedJobs.find((j: any) => j.id === batchJobId);
            if (parentJob) {
              const parentContract = matchByFilename(existingContracts, parentJob.file_name);
              if (parentContract) {
                nr.__parentId = parentContract.id;
                nr.__matchParentName = parentContract.name || parentContract.file_name || "";
              }
            }
          }
        }

        // Merge: preserve local edits, add only new results, remove approved/discarded
        const approvedIds = new Set(approvedJobs.map((j: any) => j.id));
        const discardedIds = new Set((jobs || []).filter((j: any) => j.status === 'discarded').map((j: any) => j.id));
        const locallyActed = locallyActedIdsRef.current;
        const allServerResults = [...newResults, ...failedResults].filter(r => !approvedIds.has(r.__id) && !discardedIds.has(r.__id) && !locallyActed.has(r.__id));
        setResults(prev => {
          const existingIds = new Set(prev.map((r: any) => r.__id));
          const newItems = allServerResults.filter(r => !existingIds.has(r.__id));
          const kept = prev.filter((r: any) => !approvedIds.has(r.__id) && !discardedIds.has(r.__id) && !locallyActed.has(r.__id)).map(r => {
            // Auto-resolve batch: refs in existing results too
            if (r.__parentId && String(r.__parentId).startsWith('batch:')) {
              const bjId = String(r.__parentId).replace('batch:', '');
              const pj = approvedJobs.find((j: any) => j.id === bjId);
              if (pj) { const pc = matchByFilename(existingContracts, pj.file_name); if (pc) return { ...r, __parentId: pc.id, __matchParentName: pc.name || pc.file_name || "" }; }
            }
            // Sync suggested_relationship from the server on every poll.
            // Post-processors (counterparty validation, intra-batch detector,
            // hierarchy validator) rewrite sr AFTER the job flips to
            // 'completed', so the initial snapshot is often stale. Only skip
            // the sync when the user has manually edited the parent (via
            // the search UI / dismiss button) — tracked by __userEditedParent.
            if (!r.__userEditedParent) {
              const currentJob = (jobs || []).find((j: any) => j.id === r.__id);
              const currentSr = currentJob?.extraction_result?.suggested_relationship;
              const serverPid = currentSr?.parent_id || null;
              const localPid = r.__parentId || null;
              const serverName = currentSr?.parent_name || null;
              const localName = r.__matchParentName || null;
              // Server changed the relationship (different id or different
              // name/reasoning) — pull it into local state.
              if (serverPid !== localPid || (serverPid && serverName !== localName)) {
                if (serverPid) {
                  return {
                    ...r,
                    __parentId: serverPid,
                    __relType: currentSr.relationship_type || r.__relType || 'related',
                    __matchConfidence: currentSr.confidence || r.__matchConfidence || 'medium',
                    __matchReasoning: currentSr.reasoning || '',
                    __matchParentName: serverName || '',
                  };
                } else {
                  // Server cleared the relationship (e.g. counterparty
                  // validator rejected it). Clear locally too.
                  return {
                    ...r,
                    __parentId: null,
                    __relType: null,
                    __matchConfidence: null,
                    __matchReasoning: null,
                    __matchParentName: null,
                  };
                }
              }
            }
            return r;
          });
          const merged = [...kept, ...newItems];
          // Sort: parent types (MSA etc.) before child types (Amendment, SOW etc.)
          const CHILD_KEYWORDS = ["amendment","addendum","statement of work","sow","work order","change order","exhibit","schedule"];
          merged.sort((a: any, b: any) => {
            const aType = (a["Contract Type"]?.v || "").toLowerCase();
            const bType = (b["Contract Type"]?.v || "").toLowerCase();
            const aIsChild = CHILD_KEYWORDS.some(k => aType.includes(k));
            const bIsChild = CHILD_KEYWORDS.some(k => bType.includes(k));
            if (aIsChild === bIsChild) return 0;
            return aIsChild ? 1 : -1;
          });
          return merged;
        });

        if (batch.status === 'completed' || batch.status === 'failed') {
          setProcessing(false);
          setCurrentFile("");
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }
      } catch (err: any) { console.error("Poll error:", err); }
    };
    poll();
    pollRef.current = setInterval(poll, 1000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [batchId]);

  // Auto-resolve batch: parent refs whenever existingContracts updates (e.g. after approval)
  useEffect(() => {
    if (!existingContracts.length || !batchJobs.length) return;
    const approvedJobs = batchJobs.filter(j => j.status === 'approved');
    if (!approvedJobs.length) return;
    setResults(prev => {
      let changed = false;
      const updated = prev.map((r: any) => {
        if (r.__parentId && String(r.__parentId).startsWith('batch:')) {
          const bjId = String(r.__parentId).replace('batch:', '');
          const pj = approvedJobs.find((j: any) => j.id === bjId);
          if (pj) {
            const pc = matchByFilename(existingContracts, pj.file_name);
            if (pc) { changed = true; return { ...r, __parentId: pc.id, __matchParentName: pc.name || pc.file_name || "" }; }
          }
        }
        return r;
      });
      return changed ? updated : prev;
    });
  }, [existingContracts, batchJobs]);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); setDrag(false);
    const dropped = Array.from(e.dataTransfer?.files || []).filter((f: any) => f.name.match(/\.(pdf|docx|doc)$/i));
    setFiles(p => [...p, ...dropped.map((f: any) => f.name)]);
    setRealFiles(p => [...p, ...dropped]);
    checkDuplicates(dropped.map((f: any) => f.name));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from((e.target as HTMLInputElement).files || []).filter((f: any) => f.name.match(/\.(pdf|docx|doc)$/i));
    setFiles(p => [...p, ...selected.map((f: any) => f.name)]);
    setRealFiles(p => [...p, ...selected]);
    checkDuplicates(selected.map((f: any) => f.name));
  };

  const removeFile = (idx: number) => {
    setFiles(p => p.filter((_: any, j: number) => j !== idx));
    setRealFiles(p => p.filter((_: any, j: number) => j !== idx));
    setDupeWarnings(p => p.filter(d => d.filename !== files[idx]));
  };

  const checkDuplicates = async (filenames: string[]) => {
    const token = localStorage.getItem("kontractai_token");
    try {
      const res = await fetch("/api/contracts/check-duplicate", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ filenames }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.duplicates?.length > 0) setDupeWarnings(data.duplicates);
      }
    } catch (err: any) { console.error("Dupe check failed:", err); }
  };

  const run = async () => {
    if (realFiles.length === 0) return;
    setProcessing(true); setProgress(0); setError(""); setResults([]); locallyActedIdsRef.current.clear();

    const token = localStorage.getItem("kontractai_token");
    const formData = new FormData();
    realFiles.forEach((f: any) => formData.append("files", f));
    formData.append("custom_fields", JSON.stringify(customFields));
    formData.append("client_entity", clientEntity);
    if (selectedProfileId === "__auto__") {
      formData.append("auto_detect_profile", "true");
    } else if (selectedProfileId) {
      formData.append("profile_id", selectedProfileId);
    }

    try {
      const res = await fetch("/api/extraction/batch/start", {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Server returned ${res.status}`);
      }
      const data = await res.json();
      setBatchId(data.batch_id);
      setBatchTotal(data.total_count || realFiles.length);
      setShowNavSafe(true);
    } catch (err: any) {
      setError(err.message);
      setProcessing(false);
    }
  };

  const approveResult = async (row: any) => {
    // Block low-confidence / missing / placeholder values before sending to backend
    const v = validateImportRow(row);
    if (!v.ok) {
      const parts: string[] = [];
      if (v.missing.length) parts.push(`missing: ${v.missing.join(', ')}`);
      if (v.placeholder.length) parts.push(`placeholder text in: ${v.placeholder.join(', ')}`);
      if (v.lowConf.length) parts.push(`low confidence in: ${v.lowConf.join(', ')}`);
      setResults(prev => prev.map((r: any) => r.__id === row.__id
        ? { ...r, __error: `Edit required before import — ${parts.join('; ')}`, __editing: true }
        : r));
      return;
    }
    setApprovingId(row.__id);
    try {
      // Resolve client_id
      let resolvedClientId = null;
      if (selectedClientId && selectedClientId !== "__new__") {
        resolvedClientId = parseInt(selectedClientId, 10);
      } else if (selectedClientId === "__new__" && newClientName.trim()) {
        try {
          const clientRes = await apiCall("/clients", { method: "POST", body: JSON.stringify({ name: newClientName.trim() }) });
          if (clientRes?.client?.id) resolvedClientId = clientRes.client.id;
        } catch (err: any) { console.error("Create client failed:", err); }
      }

      // Build overrides from any edited fields
      const overrides: Record<string, any> = {};
      Object.entries(row).filter(([k]) => !k.startsWith("__") && k !== "suggestedRelationship").forEach(([k, v]: [string, any]) => {
        if (v?.v) overrides[k] = { value: v.v, confidence: v.conf || "medium" };
      });

      // Use raw fetch here instead of apiCall so a 400 blocker error body
      // ("Cannot import — fix these fields first: X, Y") reaches the user
      // verbatim. apiCall swallows all non-2xx into null, which would turn
      // every backend validation message into the generic "Approval failed".
      const approveToken = localStorage.getItem("kontractai_token");
      const approveHeaders: Record<string, string> = { "Content-Type": "application/json" };
      if (approveToken) approveHeaders["Authorization"] = `Bearer ${approveToken}`;
      try {
        const ctx = sessionStorage.getItem("kontractai_client_scope");
        if (ctx && ctx !== "all") approveHeaders["X-Client-Context"] = ctx;
      } catch { /* skip */ }
      const rawRes = await fetch(`/api/extraction/batch/${row.__batchId}/job/${row.__jobId}/approve`, {
        method: "POST",
        headers: approveHeaders,
        body: JSON.stringify({
          overrides,
          parent_id: row.__parentId || null,
          relationship_type: row.__relType || "child",
          agreement_category: row.__agreementCategory || null,
          client_id: resolvedClientId,
        }),
      });
      const res = await rawRes.json().catch(() => null);
      if (!rawRes.ok) {
        throw new Error(res?.error || `Approval failed (HTTP ${rawRes.status})`);
      }

      if (res?.contract) {
        const hasExhibits = Array.isArray(row.__detectedExhibits) && row.__detectedExhibits.length > 0;
        setImportedCount(prev => prev + 1);
        if (hasExhibits) {
          // Keep row visible so user can extract exhibits using the approved contract id.
          setResults(prev => prev.map((r: any) => r.__id === row.__id
            ? { ...r, __approvedContractId: res.contract.id, __approved: true }
            : r));
        } else {
          locallyActedIdsRef.current.add(row.__id);
          setResults(prev => prev.filter((r: any) => r.__id !== row.__id));
        }
        // Refresh contracts list so amendment parent search finds newly approved contracts
        try { const refreshData = await apiCall("/contracts"); if (refreshData?.flat) setExistingContracts(refreshData.flat); } catch (_) {}
      } else {
        throw new Error(res?.error || "Approval failed");
      }
    } catch (err: any) {
      console.error("Approve failed:", err);
      setResults(prev => prev.map((r: any) => r.__id === row.__id ? { ...r, __error: `Import failed: ${err.message}` } : r));
    } finally {
      setApprovingId(null);
    }
  };

  // Extract a single template. Returns { conflict, data }. Does NOT throw on 409.
  const extractTemplateStep = async (rowId: string, exhibitIdx: number, replace: boolean = false) => {
    const row = results.find((r: any) => r.__id === rowId);
    if (!row) throw new Error("Row not found");
    const token = localStorage.getItem("kontractai_token");
    const res = await fetch(`/api/extraction/batch/${row.__batchId}/job/${row.__jobId}/extract-exhibit`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ exhibit_index: exhibitIdx, parent_contract_id: row.__approvedContractId, replace }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 409) return { conflict: true, data };
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return { conflict: false, data };
  };

  const openWizard = (data: any) => new Promise<void>((resolve) => {
    wizardResolveRef.current = () => resolve();
    setTemplateWizard(data);
  });

  const askReplaceConfirm = (data: any) => new Promise<"replace" | "skip">((resolve) => {
    replaceResolveRef.current = (choice) => resolve(choice);
    setReplaceConfirm(data);
  });

  // Serial runner: processes all pending (non-extracted) templates for a row
  // one at a time. On 409 conflict it prompts the user; on success it opens
  // the per-template wizard and awaits user confirmation before advancing.
  const runExtractAll = async (rowId: string) => {
    const row = results.find((r: any) => r.__id === rowId);
    if (!row || !Array.isArray(row.__detectedExhibits)) return;
    const pending = row.__detectedExhibits
      .map((ex: any, idx: number) => ({ idx, ex }))
      .filter((x: any) => x.ex.status !== "extracted");
    if (pending.length === 0) return;

    setExtractAllRunning(rowId);
    try {
      for (let i = 0; i < pending.length; i++) {
        const { idx, ex } = pending[i];
        setExtractAllProgress({ current: i + 1, total: pending.length, currentLabel: ex.title || ex.type });
        setExtractingExhibit(`${rowId}:${idx}`);
        setExhibitErrors(prev => { const next = { ...prev }; delete next[`${rowId}:${idx}`]; return next; });

        let result: any;
        try {
          result = await extractTemplateStep(rowId, idx, false);
        } catch (err: any) {
          setExhibitErrors(prev => ({ ...prev, [`${rowId}:${idx}`]: err.message || "Extraction failed" }));
          continue;
        }

        if (result.conflict) {
          const choice = await askReplaceConfirm({
            rowId, exhibitIdx: idx,
            exhibitTitle: ex.title || ex.type,
            existing_template_name: result.data.existing_template_name,
            existing_version: result.data.existing_version,
            next_version: result.data.next_version,
          });
          setReplaceConfirm(null);
          replaceResolveRef.current = null;
          if (choice === "skip") continue;
          try {
            result = await extractTemplateStep(rowId, idx, true);
            if (result.conflict) {
              setExhibitErrors(prev => ({ ...prev, [`${rowId}:${idx}`]: "Replace conflict" }));
              continue;
            }
          } catch (err: any) {
            setExhibitErrors(prev => ({ ...prev, [`${rowId}:${idx}`]: err.message || "Replace failed" }));
            continue;
          }
        }

        // Open wizard, wait for user to finish.
        await openWizard({
          rowId, exhibitIdx: idx,
          exhibitTitle: ex.title || ex.type,
          template_id: result.data.template_id,
          template_name: result.data.template_name,
          file_name: result.data.file_name,
          placeholder_fields: result.data.placeholder_fields || [],
          version: result.data.version,
          replaced: result.data.replaced,
        });
        setTemplateWizard(null);
        wizardResolveRef.current = null;

        // Mark exhibit extracted locally so the UI reflects it without a re-fetch.
        setResults(prev => prev.map((r: any) => {
          if (r.__id !== rowId) return r;
          const nextEx = [...(r.__detectedExhibits || [])];
          nextEx[idx] = { ...nextEx[idx], status: "extracted", template_id: result.data.template_id, template_name: result.data.template_name };
          return { ...r, __detectedExhibits: nextEx };
        }));
      }
    } finally {
      setExtractAllRunning(null);
      setExtractAllProgress(null);
      setExtractingExhibit(null);
    }
  };

  return (
    <div>
      <SectionTitle sub="Bulk import with AI metadata extraction">AI Contract Import</SectionTitle>

      {showResumeBanner && batchId && (
        <div style={{ padding: "14px 18px", backgroundColor: processing ? "rgba(43,122,62,.06)" : "rgba(26,138,158,.06)", border: `1px solid ${processing ? "rgba(43,122,62,.15)" : "rgba(26,138,158,.15)"}`, borderRadius: 8, marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: processing ? "#2B7A3E" : "#1A8A9E" }}>
              {processing ? "Extraction in progress" : "Previous batch results ready for review"}
            </div>
            <div style={{ fontSize: 12, color: "#5A6278", marginTop: 2 }}>
              {processing
                ? `Processing ${batchTotal} contracts. Results will appear below as they complete.`
                : `${results.length} extraction result${results.length !== 1 ? "s are" : " is"} ready for review and approval.`
              }
            </div>
          </div>
          <button onClick={() => { setShowResumeBanner(false); if (!processing) { setBatchId(null); setBatchJobs([]); setResults([]); setBatchTotal(0); setBatchCompleted(0); } }} style={{ background: "none", border: "none", color: "#8B93A8", cursor: "pointer", fontSize: 12, padding: "4px 8px" }}>
            {processing ? "\u2715" : "Start New Batch"}
          </button>
        </div>
      )}

      {/* Optional Client Context — hidden on in-house tenants since they
         have a single implicit client entity (the company itself). */}
      {edition !== 'in_house' && (
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>Client Entity (optional)</span>
        </div>
        <div style={{ fontSize: 11, color: T.txM, marginBottom: 8 }}>If provided, the AI will use this to correctly identify which party is the company vs. counterparty. If left blank, you can use the "Swap Parties" button on each result to correct any misidentification.</div>
        <select value={selectedClientId} onChange={e => { setSelectedClientId(e.target.value); if (e.target.value === "__new__") { setClientEntity(""); setNewClientName(""); } else if (e.target.value) { const cl = (clients || []).find((c: any) => String(c.id) === e.target.value); setClientEntity(cl?.name || ""); setNewClientName(""); } else { setClientEntity(""); setNewClientName(""); } }} style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 8 }}>
          <option value="">Select a client...</option>
          {(clients || []).map((cl: any) => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
          <option value="__new__">+ Create New Client</option>
        </select>
        {selectedClientId === "__new__" && <input value={newClientName} onChange={e => { setNewClientName(e.target.value); setClientEntity(e.target.value); }} placeholder="Enter new client name, e.g., AmplifyBio, LLC" style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />}
      </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}><div style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: T.accS, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: T.acc }}>1</div><span style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>Upload Contracts</span></div>
        <div onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={handleDrop} onClick={() => document.getElementById("bulkUp")?.click()} style={{ border: `2px dashed ${drag ? T.acc : T.brd}`, borderRadius: 8, padding: "32px 16px", textAlign: "center", backgroundColor: drag ? T.accS : T.bg3, cursor: "pointer" }}>
          <input id="bulkUp" type="file" multiple accept=".pdf,.docx,.doc" style={{ display: "none" }} onChange={handleFileSelect} />
          <div style={{ fontSize: 24, marginBottom: 6 }}>◬</div>
          <div style={{ fontSize: 13, color: T.tx, fontWeight: 600 }}>Drag & drop or <span style={{ color: T.acc }}>browse</span></div>
          <div style={{ fontSize: 10, color: T.txD, marginTop: 3 }}>PDF, DOCX, DOC</div>
        </div>
        {files.length > 0 && <div style={{ marginTop: 8, maxHeight: 100, overflow: "auto" }}>{files.map((f: any, i: number) => <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px", fontSize: 11, color: T.tx, backgroundColor: T.bg3, borderRadius: 3, marginBottom: 2 }}><span>📄 {f} <span style={{ color: T.txD }}>({(realFiles[i]?.size / 1024).toFixed(0)} KB)</span></span><button onClick={() => removeFile(i)} style={{ background: "none", border: "none", color: T.txD, cursor: "pointer" }}>✕</button></div>)}</div>}
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}><div style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: T.accS, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: T.acc }}>2</div><span style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>Extraction Fields</span></div>
        <div style={{ fontSize: 11, color: T.txM, marginBottom: 8, lineHeight: 1.5 }}>Fields are automatically selected based on contract type. Universal fields (Company, Counterparty, Dates, Type, Governing Law, Signature Date) are always extracted. Type-specific fields are added based on the detected contract type (MSA, SOW, Amendment, NDA, Vendor, License, DPA).</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
          {["MSA", "SOW", "Amendment", "NDA", "Vendor", "License", "DPA"].map(t => <Badge key={t} c={T.acc} b={T.accS}>{t}</Badge>)}
        </div>
        <div style={{ fontSize: 11, color: T.txM, marginBottom: 6 }}>Extraction profile (narrows fields to the relevant set for each contract type):</div>
        <select value={selectedProfileId} onChange={e => setSelectedProfileId(e.target.value)} style={{ width: "100%", padding: "6px 10px", borderRadius: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 11, outline: "none", fontFamily: "inherit", marginBottom: 10 }}>
          <option value="__auto__">Auto-detect contract type (two-pass — recommended)</option>
          {profiles.map((p: any) => <option key={p.id} value={p.id}>{p.name}{Array.isArray(p.fields) ? ` (${p.fields.length} fields)` : ''}{p.is_system ? ' — system' : ''}</option>)}
        </select>
        {selectedProfileId === "__auto__" && <div style={{ fontSize: 11, color: T.txM, marginBottom: 10, padding: "6px 10px", backgroundColor: T.accS, borderRadius: 4, lineHeight: 1.5 }}>Runs two passes: first identifies Contract Type, then re-extracts with the matching profile. ~1.5× normal cost per document, substantially cleaner field list.</div>}
        {selectedProfileId && selectedProfileId !== "__auto__" && (() => {
          const p = profiles.find((x: any) => x.id === selectedProfileId);
          const fieldList = p && Array.isArray(p.fields) ? p.fields : [];
          return <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>{fieldList.map((f: any, i: number) => <Badge key={i} c={T.acc} b={T.accS}>{f}</Badge>)}</div>;
        })()}
        <div style={{ fontSize: 11, color: T.txM, marginBottom: 6 }}>Custom fields (additions appended to the profile or the default set):</div>
        <div style={{ display: "flex", gap: 6 }}><input value={newField} onChange={e => setNewField(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && newField.trim()) { setCustomFields(p => [...p, newField.trim()]); setNewField(""); } }} placeholder="Add custom field..." style={{ flex: 1, padding: "6px 10px", borderRadius: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 11, outline: "none", fontFamily: "inherit" }} /><Btn v="secondary" sz="sm" onClick={() => { if (newField.trim()) { setCustomFields(p => [...p, newField.trim()]); setNewField(""); } }}>Add</Btn></div>
        {customFields.length > 0 && <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>{customFields.map((f: any, i: number) => <Badge key={i} c={T.prp} b={T.prpS}>{f} <button onClick={() => setCustomFields(p => p.filter((_: any, j: number) => j !== i))} style={{ background: "none", border: "none", color: T.prp, cursor: "pointer", fontSize: 9, padding: 0, marginLeft: 2 }}>✕</button></Badge>)}</div>}
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}><div style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: T.accS, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: T.acc }}>3</div><span style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>Run AI Extraction</span></div>
        <p style={{ fontSize: 12, color: T.txM, margin: "0 0 12px" }}>The AI will analyze each uploaded contract, extract metadata fields, identify confidence levels, and detect relationships to other agreements.</p>
        {error && <div style={{ padding: "6px 10px", backgroundColor: T.redS, borderRadius: 4, marginBottom: 10, fontSize: 11, color: T.red }}>{error}</div>}
        {dupeWarnings.length > 0 && <div style={{ marginBottom: 12, padding: "10px 14px", backgroundColor: "rgba(208,64,64,.08)", border: "1px solid rgba(208,64,64,.2)", borderRadius: 6 }}><div style={{ fontSize: 12, fontWeight: 700, color: "#D04040", marginBottom: 4 }}>Duplicate Warning</div>{dupeWarnings.map((d: any, i: number) => <div key={i} style={{ fontSize: 11, color: "#5A6278", marginBottom: 2 }}><strong>{d.filename}</strong> already exists as: {d.existing_name}</div>)}<div style={{ fontSize: 10, color: "#8B93A8", marginTop: 4 }}>Remove duplicate files or proceed. Duplicates will be blocked at import.</div></div>}
        <Btn v="primary" sz="lg" onClick={() => { run(); }} disabled={files.length === 0 || processing}>
          {processing ? `Processing${batchTotal ? ` (${batchTotal} contracts)` : ''}...` : `Extract from ${files.length} contract${files.length !== 1 ? "s" : ""}`}
        </Btn>
        {processing && <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.tx }}>{currentFile}</span>
            <span style={{ fontSize: 11, color: T.txM }}>{batchCompleted} of {batchTotal || realFiles.length} complete ({progress}%)</span>
          </div>
          <div style={{ height: 4, borderRadius: 2, backgroundColor: T.bg4, overflow: "hidden" }}><div style={{ width: `${progress}%`, height: "100%", backgroundColor: T.acc, transition: "width .3s", borderRadius: 2 }} /></div>
          {batchJobs.length > 0 && <div style={{ marginTop: 10, maxHeight: 300, overflowY: "auto", border: `1px solid ${T.brd}`, borderRadius: 6, backgroundColor: T.bg3 }}>
            {batchJobs.map(j => {
              const stage = j.progress_stage || (j.status === 'completed' ? 'complete' : j.status === 'failed' ? 'failed' : j.status === 'approved' ? 'approved' : j.status === 'discarded' ? 'discarded' : j.status === 'processing' ? 'analyzing' : 'queued');
              const stagePct = ({ queued: 0, pending: 0, reading: 10, converting: 20, sections: 30, sending: 40, analyzing: 55, parsing: 75, extracting: 80, detecting: 90, complete: 100, completed: 100, failed: 100, approved: 100, discarded: 100 } as Record<string, number>)[stage] || 0;
              const stageLabel = (({ queued: "Queued", pending: "Queued", reading: "Reading document...", converting: "Extracting text from PDF...", sections: "Detecting section structure...", sending: "Sending to AI provider...", analyzing: "AI analyzing contract...", parsing: "Parsing AI response...", extracting: "Structuring extracted fields...", detecting: "Detecting relationships...", complete: "Complete", completed: "Complete", failed: "Failed", approved: "Approved", discarded: "Discarded" }) as Record<string, string>)[stage] || stage;
              const isActive = stage === 'reading' || stage === 'converting' || stage === 'sections' || stage === 'sending' || stage === 'analyzing' || stage === 'parsing' || stage === 'extracting' || stage === 'detecting';
              const isDone = stage === 'complete' || stage === 'completed' || stage === 'approved';
              const isFailed = stage === 'failed';
              const isDiscarded = stage === 'discarded';
              const barColor = isFailed ? T.red : isDiscarded ? T.txD : isDone ? T.grn : T.acc;
              return (
                <div key={j.id} style={{ padding: "8px 12px", borderBottom: `1px solid ${T.brd}`, opacity: stage === 'queued' || stage === 'pending' ? 0.5 : 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: isActive ? 600 : 400, color: isActive ? T.tx : T.txM, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "55%" }}>{isDone ? "✓ " : isFailed ? "✗ " : ""}{j.file_name}</span>
                    <span style={{ fontSize: 10, color: isFailed ? T.red : isActive ? T.acc : T.txD, fontWeight: isActive ? 600 : 400 }}>{stageLabel}{isActive ? ` ${stagePct}%` : ""}</span>
                  </div>
                  <div style={{ height: 3, borderRadius: 2, backgroundColor: T.bg4, overflow: "hidden" }}>
                    <div style={{ width: `${stagePct}%`, height: "100%", backgroundColor: barColor, transition: "width .5s ease", borderRadius: 2 }} />
                  </div>
                </div>
              );
            })}
          </div>}
          <div style={{ fontSize: 10, color: T.txD, marginTop: 6 }}>Contracts are processed sequentially to ensure accuracy</div>
        </div>}
        {showNavSafe && processing && (
          <div style={{ padding: "12px 16px", backgroundColor: "rgba(43,122,62,.08)", border: "1px solid rgba(43,122,62,.2)", borderRadius: 8, marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#2B7A3E" }}>Safe to navigate away</div>
              <div style={{ fontSize: 12, color: "#5A6278", marginTop: 2 }}>Your extraction is running on the server. You can use other sections of the application and return here to review results when ready.</div>
            </div>
            <button onClick={() => setShowNavSafe(false)} style={{ background: "none", border: "none", color: "#8B93A8", cursor: "pointer", fontSize: 16, padding: "4px 8px" }}>{"\u2715"}</button>
          </div>
        )}
      </Card>

      {(results.length > 0 || importedCount > 0) && (
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: T.grnS, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: T.grn }}>4</div><span style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>Review & Approve</span></div>
            <span style={{ fontSize: 11, color: T.txM }}>{importedCount > 0 && `${importedCount} imported`}{importedCount > 0 && results.length > 0 && ", "}{results.length > 0 && `${results.length} remaining`}</span>
          </div>
          {results.map((row: any) => {
            const isEditing = row.__editing;
            const rowContractType = (row["Contract Type"]?.v || "").toLowerCase();
            const CHILD_KW = ["amendment","addendum","statement of work","sow","work order","change order","exhibit","schedule","task order"];
            const isChildType = CHILD_KW.some(t => rowContractType.includes(t));
            const cpOverlap = (a: any, b: any) => { const aNames = [(a["Counterparty Legal Name"]?.v || "").toLowerCase(), (a["Company Legal Name"]?.v || "").toLowerCase()].filter(Boolean); const bNames = [(b["Counterparty Legal Name"]?.v || "").toLowerCase(), (b["Company Legal Name"]?.v || "").toLowerCase()].filter(Boolean); return aNames.some(an => bNames.some(bn => { const af = an.split(" ")[0]; const bf = bn.split(" ")[0]; return af.length > 1 && bf.length > 1 && af === bf; })); };
            const batchHasParent = isChildType && results.some((r: any) => r.__id !== row.__id && r.__jobId && ["master services agreement","msa","master agreement","services agreement","vendor agreement","professional services agreement"].some(t => (r["Contract Type"]?.v || "").toLowerCase().includes(t)) && cpOverlap(r, row));
            const parentIsConfirmed = row.__parentId && !String(row.__parentId).startsWith('batch:');
            const needsParentFirst = isChildType && !parentIsConfirmed;
            const validation = validateImportRow(row);
            const blockTitle = validation.ok ? '' : `Edit required before import:\n${[
              validation.missing.length ? `• Missing: ${validation.missing.join(', ')}` : '',
              validation.placeholder.length ? `• Placeholder text: ${validation.placeholder.join(', ')}` : '',
              validation.lowConf.length ? `• Low confidence: ${validation.lowConf.join(', ')}` : '',
            ].filter(Boolean).join('\n')}`;
            return (
            <Card key={row.__id} style={{ marginBottom: 8, padding: 14, borderLeft: row.__error ? `3px solid ${T.red}` : isEditing ? `3px solid ${T.ylw}` : `3px solid ${T.acc}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span>📄</span><span style={{ fontSize: 12, fontWeight: 700, color: T.acc }}>{row.__file}</span>
                {row.__error && <Badge c={T.red} b={T.redS}>Error: {row.__error}</Badge>}
                {row.__error && row.__retryable && (
                  <Btn v="secondary" sz="sm" disabled={retryingId === row.__id} onClick={async () => {
                    setRetryingId(row.__id);
                    try {
                      const retryRes = await apiCall(`/extraction/batch/${row.__batchId}/job/${row.__jobId}/retry`, { method: "POST" });
                      if (retryRes?.job_id) {
                        locallyActedIdsRef.current.add(row.__id);
                        setResults(prev => prev.filter((r: any) => r.__id !== row.__id));
                      } else {
                        setResults(prev => prev.map((r: any) => r.__id === row.__id ? { ...r, __error: retryRes?.error || "Retry failed" } : r));
                      }
                    } catch (e: any) {
                      setResults(prev => prev.map((r: any) => r.__id === row.__id ? { ...r, __error: "Retry request failed" } : r));
                    } finally { setRetryingId(null); }
                  }}>{retryingId === row.__id ? "Retrying..." : "Retry extraction"}</Btn>
                )}
                {row.__error && (
                  <Btn v="danger" sz="sm" onClick={async () => {
                    if (!window.confirm("Discard this extraction entirely?")) return;
                    locallyActedIdsRef.current.add(row.__id);
                    if (row.__batchId && row.__jobId) {
                      await apiCall(`/extraction/batch/${row.__batchId}/job/${row.__jobId}/discard`, { method: "POST" }).catch(() => {});
                    }
                    setResults(p => p.filter((r: any) => r.__id !== row.__id));
                  }}>Discard</Btn>
                )}
                {isEditing && <Badge c={T.ylw} b={T.ylwS}>Editing</Badge>}
                {!row.__error && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginLeft: 4 }}>
                    <select value={row.__agreementCategory || "Customer"} onChange={e => setResults(prev => prev.map((r: any) => r.__id === row.__id ? { ...r, __agreementCategory: e.target.value } : r))}
                      style={{ padding: "2px 6px", fontSize: 10, borderRadius: 4, border: `1px solid ${T.brd}`, backgroundColor: row.__agreementCategory === "Customer" ? T.grnS : row.__agreementCategory === "Vendor" ? T.prpS : T.cynS, color: row.__agreementCategory === "Customer" ? T.grn : row.__agreementCategory === "Vendor" ? T.prp : T.cyn, fontWeight: 600, outline: "none", fontFamily: "inherit", cursor: "pointer" }}>
                      <option value="Customer">Customer</option>
                      <option value="Vendor">Vendor</option>
                      <option value="Corporate">Corporate</option>
                    </select>
                  </div>
                )}
                {(row.suggestedRelationship || row.__parentId) && <Badge c={T.orn} b={T.ornS}>⚡ Relationship Detected</Badge>}
                {!row.__error && !row.__approved && <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                  {isEditing ? (
                    <>
                      <Btn v="success" sz="sm" onClick={() => {
                        // "Save Edits" acknowledges remaining low-conf fields by promoting them to
                        // 'high' (the act of entering edit mode + explicit save is the human review
                        // acknowledgment) and exits edit mode. It does NOT import — the operator
                        // must click "Approve & Import" as a separate, explicit step. Keeps the
                        // save/import actions decoupled so there is no ambiguity about when the
                        // record hits the repository.
                        const promoted: any = { ...row, __editing: false };
                        Object.keys(promoted).forEach(k => {
                          if (k.startsWith('__') || k === 'suggestedRelationship') return;
                          const field = promoted[k];
                          if (field && typeof field === 'object' && (field.conf || '').toLowerCase() === 'low') {
                            const v = (field.v == null ? '' : String(field.v)).trim();
                            if (!v) return; // empty+low is the AI's N/A signal — preserve as-is
                            promoted[k] = { ...field, conf: 'high', __userAcknowledged: true };
                          }
                        });
                        setResults(p => p.map(r => r.__id === row.__id ? promoted : r));
                      }} disabled={approvingId === row.__id}>Save Edits</Btn>
                      <Btn v="ghost" sz="sm" onClick={() => setResults(p => p.map(r => r.__id === row.__id ? { ...r, __editing: false } : r))}>Cancel Edit</Btn>
                    </>
                  ) : (
                    <>
                      <Btn v="primary" sz="sm" onClick={() => {
                        const company = row["Company Legal Name"];
                        const counter = row["Counterparty Legal Name"];
                        setResults(p => p.map(r => r.__id === row.__id ? { ...r, "Company Legal Name": counter, "Counterparty Legal Name": company } : r));
                      }} style={{ backgroundColor: "rgba(167,139,250,.15)", color: T.prp }}>⇄ Swap Parties</Btn>
                      <span title={blockTitle} style={{ display: 'inline-block' }}>
                        <Btn v="success" sz="sm" onClick={() => approveResult(row)} disabled={approvingId === row.__id || needsParentFirst || !validation.ok}>{approvingId === row.__id ? "Importing..." : !validation.ok ? `Fix ${validation.blocking.length} field${validation.blocking.length === 1 ? '' : 's'}` : "Approve & Import"}</Btn>
                      </span>
                      <Btn v="secondary" sz="sm" onClick={() => setResults(p => p.map(r => r.__id === row.__id ? { ...r, __editing: true } : r))}>Edit</Btn>
                      <Btn v="danger" sz="sm" onClick={async () => { if (window.confirm("Discard this extraction entirely?")) { locallyActedIdsRef.current.add(row.__id); if (row.__batchId && row.__jobId) { await apiCall(`/extraction/batch/${row.__batchId}/job/${row.__jobId}/discard`, { method: "POST" }).catch(() => {}); } setResults(p => p.filter(r => r.__id !== row.__id)); } }}>Discard</Btn>
                    </>
                  )}
                </div>}
              </div>
              {/* Relationship linking UI */}
              {!row.__error && !row.__approved && (() => {
                const showSearch = row.__showSearch;
                const matchedContract = row.__parentId ? existingContracts.find((c: any) => c.id === row.__parentId) : null;
                const treeParent = row.__parentId ? existingTree.find((c: any) => c.id === row.__parentId) : null;
                const searchTerm = parentSearch[row.__id] || "";
                const searchResults = searchTerm.length >= 2 ? existingContracts.filter((c: any) => {
                  if ((c.status || 'active') === 'superseded') return false;
                  const q = searchTerm.toLowerCase();
                  return (c.name || "").toLowerCase().includes(q) || (c.company_entity || "").toLowerCase().includes(q) || (c.counterparty_entity || "").toLowerCase().includes(q);
                }).slice(0, 5) : [];

                const relTypeSelect = (
                  <select value={row.__relType || "child"} onChange={e => setResults(p => p.map(r => r.__id === row.__id ? { ...r, __relType: e.target.value } : r))} style={{ padding: "4px 8px", fontSize: 10, borderRadius: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg2, color: T.tx }}>
                    <option value="child">Child Agreement</option>
                    <option value="amendment_to_parent">Amendment</option>
                    <option value="change_order">Change Order</option>
                    <option value="superseded">Supersedes</option>
                    <option value="related">Related</option>
                  </select>
                );

                const hierarchyPreview = (parent: any) => parent?.children?.length > 0 ? (
                  <div style={{ marginLeft: 16, borderLeft: `2px solid ${T.brd}`, paddingLeft: 8, marginTop: 4 }}>
                    {parent.children.map((ch: any) => <div key={ch.id} style={{ fontSize: 10, color: T.txM, marginBottom: 2 }}>{ch.name} <span style={{ color: T.txD }}>({ch.contract_type})</span></div>)}
                  </div>
                ) : null;

                const searchUI = (
                  <div style={{ marginTop: 6 }}>
                    <input value={searchTerm} onChange={e => setParentSearch((p: any) => ({ ...p, [row.__id]: e.target.value }))} placeholder="Search existing contracts..." style={{ width: "100%", padding: "6px 10px", borderRadius: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 11, outline: "none", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 4 }} />
                    {searchResults.map(c => {
                      const treeNode = existingTree.find((t: any) => t.id === c.id);
                      return (
                        <div key={c.id} onClick={() => { setResults(p => p.map(r => r.__id === row.__id ? { ...r, __parentId: c.id, __matchParentName: c.name, __showSearch: false, __userEditedParent: true } : r)); setParentSearch((p: any) => ({ ...p, [row.__id]: "" })); }} style={{ backgroundColor: T.bg2, border: `1px solid ${T.brd}`, borderRadius: 5, padding: 8, cursor: "pointer", marginBottom: 4 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 11, color: T.tx, fontWeight: 600 }}>{c.name}</span>
                            <Badge c={T.acc} b={T.accS} size="sm">{c.contract_type || "Unknown"}</Badge>
                            <span style={{ fontSize: 10, color: T.txD }}>{c.counterparty_entity}</span>
                          </div>
                          {hierarchyPreview(treeNode)}
                        </div>
                      );
                    })}
                    {searchTerm.length >= 2 && searchResults.length === 0 && <div style={{ fontSize: 10, color: T.txD, padding: 4 }}>No contracts found</div>}
                  </div>
                );

                // Case S: Same-type supersede candidate (from detectSameTypeSupersede).
                // Rendered before Case A/B so the amber styling + supersede wording
                // takes precedence over the generic parent-child banners even when
                // the match is high-confidence.
                if (row.__relType === "superseded" && row.__parentId && !showSearch) {
                  const priorType = matchedContract?.contract_type || "contract";
                  const priorName = matchedContract?.name || row.__matchParentName;
                  const priorDate = matchedContract?.effective_date ? String(matchedContract.effective_date).split("T")[0] : null;
                  const isHigh = row.__matchConfidence === "high";
                  return (
                    <div style={{ padding: "8px 12px", backgroundColor: T.ornS, border: `1px solid ${T.orn}40`, borderRadius: 6, marginBottom: 10, fontSize: 11 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                        <span style={{ color: T.orn, fontWeight: 700 }}>This replaces the prior {priorType} with same counterparty</span>
                        {relTypeSelect}
                        <Badge c={T.orn} b={T.ornS}>WILL SUPERSEDE ON APPROVE</Badge>
                        <button onClick={() => setResults(p => p.map(r => r.__id === row.__id ? { ...r, __showSearch: true, __parentId: undefined } : r))} style={{ background: "none", border: "none", color: T.txD, cursor: "pointer", fontSize: 10, marginLeft: "auto" }}>Change</button>
                        <button onClick={() => setResults(p => p.map(r => r.__id === row.__id ? { ...r, __parentId: null, __relType: null, __matchConfidence: null, __matchReasoning: null, __matchParentName: null, suggestedRelationship: null, __userEditedParent: true } : r))} style={{ background: "none", border: "none", color: T.red, cursor: "pointer", fontSize: 10 }}>Dismiss</button>
                      </div>
                      <div style={{ color: T.tx, fontSize: 11, fontWeight: 600, marginBottom: 2 }}>{priorName}{priorDate ? <span style={{ color: T.txM, fontWeight: 400 }}> — executed {priorDate}</span> : null}</div>
                      <div style={{ color: T.txM, fontSize: 10 }}>{row.__matchReasoning}</div>
                      {!isHigh && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                          <Btn v="success" sz="sm" onClick={() => setResults(p => p.map(r => r.__id === row.__id ? { ...r, __matchConfidence: "high" } : r))}>Confirm Supersede</Btn>
                          <Btn v="ghost" sz="sm" onClick={() => setResults(p => p.map(r => r.__id === row.__id ? { ...r, __showSearch: true, __parentId: undefined } : r))}>Search Different</Btn>
                        </div>
                      )}
                    </div>
                  );
                }

                // Case A: High confidence match from AI
                if (row.__parentId && row.__matchConfidence === "high" && !showSearch) {
                  return (
                    <div style={{ padding: "8px 12px", backgroundColor: T.grnS, border: `1px solid ${T.grn}40`, borderRadius: 6, marginBottom: 10, fontSize: 11 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <span style={{ color: T.grn, fontWeight: 700 }}>Matched to: {matchedContract?.name || row.__matchParentName} ({matchedContract?.contract_type || ""})</span>
                        {relTypeSelect}
                        {String(row.__parentId).startsWith('batch:') ? <Badge c={T.orn} b={T.ornS}>BATCH SIBLING</Badge> : <Badge c={T.grn} b={T.grnS}>WILL LINK ON APPROVE</Badge>}
                        <button onClick={() => setResults(p => p.map(r => r.__id === row.__id ? { ...r, __showSearch: true, __parentId: undefined } : r))} style={{ background: "none", border: "none", color: T.txD, cursor: "pointer", fontSize: 10, marginLeft: "auto" }}>Change</button>
                        <button onClick={() => setResults(p => p.map(r => r.__id === row.__id ? { ...r, __parentId: null, __relType: null, __matchConfidence: null, __matchReasoning: null, __matchParentName: null, suggestedRelationship: null, __userEditedParent: true } : r))} style={{ background: "none", border: "none", color: T.red, cursor: "pointer", fontSize: 10 }}>Dismiss</button>
                      </div>
                      <div style={{ color: T.txM, fontSize: 10 }}>{row.__matchReasoning}</div>
                      {hierarchyPreview(treeParent)}
                    </div>
                  );
                }

                // Case B: Medium/low confidence match from AI
                if (row.__parentId && row.__matchConfidence !== "high" && !showSearch) {
                  return (
                    <div style={{ padding: "8px 12px", backgroundColor: T.ylwS, border: `1px solid ${T.ylw}40`, borderRadius: 6, marginBottom: 10, fontSize: 11 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <span style={{ color: T.ylw, fontWeight: 700 }}>Possible match: {matchedContract?.name || row.__matchParentName}</span>
                        <span style={{ color: T.txM, fontSize: 10 }}>— {row.__matchReasoning}</span>
                      </div>
                      {hierarchyPreview(treeParent)}
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                        {relTypeSelect}
                        <Btn v="success" sz="sm" onClick={() => setResults(p => p.map(r => r.__id === row.__id ? { ...r, __matchConfidence: "high" } : r))}>Confirm</Btn>
                        <Btn v="ghost" sz="sm" onClick={() => setResults(p => p.map(r => r.__id === row.__id ? { ...r, __showSearch: true, __parentId: undefined } : r))}>Search Different</Btn>
                        <Btn v="ghost" sz="sm" onClick={() => setResults(p => p.map(r => r.__id === row.__id ? { ...r, __parentId: null, __relType: null, __matchConfidence: null, __matchReasoning: null, __matchParentName: null, suggestedRelationship: null, __userEditedParent: true } : r))} style={{ color: T.red }}>Dismiss Match</Btn>
                      </div>
                    </div>
                  );
                }

                // Case C: Legacy string suggestion or overridden search
                if (row.suggestedRelationship || showSearch) {
                  return (
                    <div style={{ marginBottom: 10 }}>
                      {row.suggestedRelationship && typeof row.suggestedRelationship === "string" && <div style={{ padding: "6px 10px", backgroundColor: T.ornS, borderRadius: 4, marginBottom: 6, fontSize: 11, color: T.orn }}>AI: {row.suggestedRelationship}</div>}
                      <div style={{ padding: "8px 10px", backgroundColor: T.bg3, borderRadius: 4, border: `1px solid ${T.brd}` }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: T.txM, marginBottom: 4 }}>Search for Parent Contract</div>
                        {row.__parentId && (
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, padding: "6px 8px", backgroundColor: T.bg2, borderRadius: 4, borderLeft: `3px solid ${T.grn}` }}>
                            <span style={{ fontSize: 11, color: T.tx, fontWeight: 600 }}>{matchedContract?.name || row.__matchParentName}</span>
                            {relTypeSelect}
                            <Badge c={T.grn} b={T.grnS}>Will link on approve</Badge>
                            <button onClick={() => setResults(p => p.map(r => r.__id === row.__id ? { ...r, __parentId: undefined, __matchParentName: undefined } : r))} style={{ background: "none", border: "none", color: T.txD, cursor: "pointer", fontSize: 10, marginLeft: "auto" }}>✕</button>
                          </div>
                        )}
                        {!row.__parentId && searchUI}
                      </div>
                    </div>
                  );
                }

                // Case D: No relationship detected — show nothing
                return null;
              })()}
              {!row.__error && <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                {(() => {
                  // Build field list from extracted data. When auto-detect identified
                  // a profile, render profile-specific fields first (in profile order),
                  // then universals, then anything else. Otherwise fall back to
                  // universals-first ordering.
                  const rowFields = Object.keys(row).filter(k => !k.startsWith("__") && k !== "suggestedRelationship" && row[k]?.v);
                  const allDisplayFields = [...new Set([...rowFields, ...customFields])];
                  const detectedProfileId = row.__detectedProfileId;
                  const detectedProfile = detectedProfileId ? profiles.find((p: any) => p.id === detectedProfileId) : null;
                  const profileFields: string[] = detectedProfile && Array.isArray(detectedProfile.fields) ? detectedProfile.fields : [];
                  const profileFieldSet = new Set(profileFields);
                  const universalSet = new Set(AI_UNIVERSAL_FIELDS);
                  const uIdx = (f: string) => { const i = AI_UNIVERSAL_FIELDS.indexOf(f); return i >= 0 ? i : 999; };
                  const pIdx = (f: string) => profileFields.indexOf(f);
                  allDisplayFields.sort((a: any, b: any) => {
                    const aP = profileFieldSet.has(a), bP = profileFieldSet.has(b);
                    if (aP !== bP) return aP ? -1 : 1;
                    if (aP && bP) return pIdx(a) - pIdx(b);
                    const aU = universalSet.has(a), bU = universalSet.has(b);
                    if (aU !== bU) return aU ? -1 : 1;
                    if (aU && bU) return uIdx(a) - uIdx(b);
                    return a.localeCompare(b);
                  });
                  return allDisplayFields;
                })().map(f => {
                  let val = row[f];
                  if (!val && !isEditing) return null;
                  // Normalize: if val is a string, wrap it
                  if (typeof val === "string") val = { v: val, conf: "medium" };
                  if (val && typeof val === "object" && !val.v && val.value) val = { v: val.value, conf: val.confidence || "medium" };
                  if (!val || (!val.v && !isEditing)) return null;
                  if (!isEditing && val.v && (val.v === "N/A" || val.v === "null" || val.v.trim() === "")) return null;
                  const confLevel = val.conf || val.confidence || "medium";
                  return (
                  <div key={f} style={{ padding: "6px 8px", backgroundColor: isEditing ? T.bg4 : T.bg3, borderRadius: 4, border: isEditing ? `1px solid ${T.brd}` : "1px solid transparent" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}><span style={{ fontSize: 9, color: T.txD, textTransform: "uppercase" }}>{f}</span>{val && <ConfBadge level={confLevel} />}</div>
                    {isEditing && f === 'Contract Type' ? (() => {
                      const currentVal = val?.v || '';
                      const aiCategory = (row['Agreement Category']?.v || row['Agreement Category']?.value || '').trim();
                      const categoriesInOrder = Object.keys(contractTypesByCategory).sort();
                      const allNames = allContractTypeNames;
                      const isRecognized = !currentVal || allNames.includes(currentVal);
                      return (
                        <div>
                          <select
                            value={isRecognized ? currentVal : ''}
                            onChange={(e) => {
                              const newVal = e.target.value;
                              setResults(p => p.map(r => r.__id === row.__id ? { ...r, [f]: { ...r[f], v: newVal, conf: 'high', __userEdited: true, needs_type_review: false, __original_ai_value: undefined } } : r));
                            }}
                            style={{ width: "100%", padding: "4px 6px", borderRadius: 3, border: `1px solid ${isRecognized ? T.brd : T.ylwS}`, backgroundColor: T.bg3, color: T.tx, fontSize: 11, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                          >
                            <option value="">— select a contract type —</option>
                            {aiCategory && contractTypesByCategory[aiCategory] && (
                              <optgroup label={`${aiCategory} (extracted category)`}>
                                {contractTypesByCategory[aiCategory].map(n => <option key={`${aiCategory}-${n}`} value={n}>{n}</option>)}
                              </optgroup>
                            )}
                            {categoriesInOrder.filter(c => c !== aiCategory).map(c => (
                              <optgroup key={c} label={c}>
                                {contractTypesByCategory[c].map(n => <option key={`${c}-${n}`} value={n}>{n}</option>)}
                              </optgroup>
                            ))}
                          </select>
                          {!isRecognized && (
                            <div style={{ marginTop: 4, fontSize: 10, color: T.ylw }}>
                              ⚠ Unrecognized type: <span style={{ fontFamily: 'monospace' }}>{currentVal}</span> — please pick an approved type.
                            </div>
                          )}
                          {val?.needs_type_review && isRecognized && (
                            <div style={{ marginTop: 4, fontSize: 10, color: T.txD }}>
                              AI couldn't match the document to the approved list. Confirm the selection above.
                            </div>
                          )}
                        </div>
                      );
                    })() : isEditing && f === 'Agreement Category' ? (
                      <select
                        value={val?.v || ''}
                        onChange={(e) => {
                          const newVal = e.target.value;
                          setResults(p => p.map(r => r.__id === row.__id ? { ...r, [f]: { ...r[f], v: newVal, conf: 'high', __userEdited: true } } : r));
                        }}
                        style={{ width: "100%", padding: "4px 6px", borderRadius: 3, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 11, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                      >
                        <option value="">— select —</option>
                        <option value="Customer">Customer</option>
                        <option value="Vendor">Vendor</option>
                        <option value="Corporate">Corporate</option>
                      </select>
                    ) : isEditing ? (
                      <input
                        defaultValue={val?.v || ""}
                        onChange={(e) => {
                          const newVal = e.target.value;
                          setResults(p => p.map(r => r.__id === row.__id ? { ...r, [f]: newVal.trim()
                            ? { ...r[f], v: newVal, conf: 'high', __userEdited: true }
                            : { ...r[f], v: '', conf: 'low', __userEdited: false } } : r));
                        }}
                        style={{ width: "100%", padding: "4px 6px", borderRadius: 3, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 11, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                      />
                    ) : (
                      <div style={{ fontSize: 11, color: T.tx }}>{val?.v || ""}</div>
                    )}
                    {(val?.src || val?.v) && !isEditing && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                        {val.srcSec && <span style={{ fontSize: 9, color: T.txD }}>§{val.srcSec}</span>}
                        <button onClick={async (e) => { e.stopPropagation(); const fi = realFiles.find((rf: any) => rf.name === row.__file); if (fi) { const reader = new FileReader(); reader.onload = () => { const b64 = (reader.result as string).split(',')[1]; setPdfHighlight({ file: fi, searchText: val.src || val.v, sectionRef: val.srcSec, base64: b64, fieldName: f, fieldValue: val.v }); }; reader.readAsDataURL(fi); } else if (row.__batchId && row.__jobId) { try { const resp = await fetch(`/api/extraction/batch/${row.__batchId}/job/${row.__jobId}/file`, { headers: { Authorization: `Bearer ${localStorage.getItem("kontractai_token")}` } }); if (!resp.ok) { alert("PDF file not found on server."); return; } const blob = await resp.blob(); const file = new File([blob], row.__file || "document.pdf", { type: blob.type }); const b64 = await new Promise(r => { const rd = new FileReader(); rd.onload = () => r((rd.result as string).split(',')[1]); rd.readAsDataURL(blob); }); setPdfHighlight({ file, searchText: val.src || val.v, sectionRef: val.srcSec, base64: b64, fieldName: f, fieldValue: val.v }); } catch (err: any) { alert("Could not load PDF: " + err.message); } } else { alert("PDF file not available."); } }} style={{ fontSize: 9, color: T.acc, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", padding: 0 }}>View in document</button>
                      </div>
                    )}
                  </div>
                ); })}
              </div>}
              {(() => {
                const hasParent = !!row.__parentId;
                if (isChildType && hasParent) {
                  const parentContract = existingContracts.find((c: any) => c.id === row.__parentId);
                  return (
                    <div style={{ marginTop: 10, padding: 10, backgroundColor: "rgba(45,160,80,.06)", border: "1px solid rgba(45,160,80,.2)", borderRadius: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: T.grn }}>Linked to Parent Agreement</div>
                        <div style={{ fontSize: 11, color: T.tx }}>{parentContract?.name || row.__matchParentName || "Selected"}</div>
                      </div>
                      <Btn v="ghost" sz="sm" onClick={() => setResults(prev => prev.map((r: any) => r.__id === row.__id ? { ...r, __parentId: null, __relType: null } : r))}>Change</Btn>
                    </div>
                  );
                }
                if (needsParentFirst) {
                  const batchSiblingParent = results.find((r: any) => r.__id !== row.__id && r.__jobId && ["master services agreement","msa","master agreement","services agreement","vendor agreement","professional services agreement"].some(t => (r["Contract Type"]?.v || "").toLowerCase().includes(t)) && cpOverlap(r, row));
                  return (
                    <div style={{ marginTop: 10, padding: 12, backgroundColor: "rgba(208,64,64,.06)", border: "1px solid rgba(208,64,64,.2)", borderRadius: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.red, marginBottom: 6 }}>{row["Contract Type"]?.v || "Child Agreement"} Requires Parent Agreement</div>
                      {batchSiblingParent ? (
                        <div style={{ fontSize: 11, color: T.txM, marginBottom: 8, padding: "8px 10px", backgroundColor: "rgba(26,138,158,.06)", border: "1px solid rgba(26,138,158,.15)", borderRadius: 4 }}>
                          The parent agreement appears to be in this batch: <strong>{batchSiblingParent["Company Legal Name"]?.v} / {batchSiblingParent["Counterparty Legal Name"]?.v} — {batchSiblingParent["Contract Type"]?.v}</strong>. Approve the parent agreement first, then this record can be linked automatically.
                        </div>
                      ) : (
                        <div style={{ fontSize: 11, color: T.txM, marginBottom: 8 }}>This {row["Contract Type"]?.v || "child agreement"} must be linked to its parent agreement before import. Select the parent agreement below, or discard if the parent does not exist.</div>
                      )}
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <input placeholder="Search for parent agreement..." value={parentSearch[row.__id] || ""} onChange={e => setParentSearch((p: any) => ({ ...p, [row.__id]: e.target.value }))} style={{ flex: 1, padding: "6px 8px", fontSize: 11, borderRadius: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, outline: "none", fontFamily: "inherit" }} />
                      </div>
                      {parentSearch[row.__id]?.length >= 2 && (
                        <div style={{ maxHeight: 150, overflow: "auto", marginTop: 6 }}>
                          {existingContracts.filter((c: any) => { if ((c.status || 'active') === 'superseded') return false; const s = (parentSearch[row.__id] || "").toLowerCase(); return (c.name || "").toLowerCase().includes(s) || (c.counterparty_entity || "").toLowerCase().includes(s); }).map(c => (
                            <div key={c.id} onClick={() => { setResults(prev => prev.map((r: any) => r.__id === row.__id ? { ...r, __parentId: c.id, __relType: rowContractType.includes("amendment") ? "amendment_to_parent" : "child" } : r)); setParentSearch((p: any) => ({ ...p, [row.__id]: "" })); }} style={{ padding: "6px 8px", fontSize: 11, color: T.tx, cursor: "pointer", borderRadius: 3, backgroundColor: T.bg3, marginBottom: 2 }}>
                              <div style={{ fontWeight: 500 }}>{c.name}</div>
                              <div style={{ fontSize: 10, color: T.txD }}>{c.contract_type} · {c.counterparty_entity}</div>
                            </div>
                          ))}
                          {existingContracts.filter((c: any) => { if ((c.status || 'active') === 'superseded') return false; const s = (parentSearch[row.__id] || "").toLowerCase(); return (c.name || "").toLowerCase().includes(s) || (c.counterparty_entity || "").toLowerCase().includes(s); }).length === 0 && <div style={{ fontSize: 11, color: T.txD, padding: "6px 8px" }}>No matching agreements found</div>}
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
              {/* Exhibit templates detected banner — single Extract All button + per-template status */}
              {Array.isArray(row.__detectedExhibits) && row.__detectedExhibits.length > 0 && (
                <div data-deploy-marker="EXHIBIT_UI_W5_20260416" style={{ marginTop: 12, padding: 12, backgroundColor: "rgba(26,138,158,.06)", border: "1px solid rgba(26,138,158,.2)", borderRadius: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: 14 }}>📋</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#1A8A9E" }}>Exhibit Templates Detected ({row.__detectedExhibits.length})</span>
                  </div>
                  <div style={{ fontSize: 11, color: T.txM, marginBottom: 10 }}>
                    This agreement contains reusable exhibit forms. Extracting saves each as a counterparty-specific template. After each extraction you will confirm placeholder mapping and SharePoint save.
                    {!row.__approvedContractId && <span style={{ display: "block", marginTop: 4, color: T.txD }}>Approve the parent contract first to enable extraction.</span>}
                  </div>
                  {(() => {
                    const pendingCount = row.__detectedExhibits.filter((e: any) => e.status !== "extracted").length;
                    const allExtracted = pendingCount === 0;
                    const running = extractAllRunning === row.__id;
                    const prog = running && extractAllProgress ? extractAllProgress : null;
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                        {allExtracted ? (
                          <Badge c={T.grn} b={T.grnS}>✓ All {row.__detectedExhibits.length} template(s) extracted</Badge>
                        ) : (
                          <>
                            <span title={!row.__approvedContractId ? "Approve the parent contract first" : ""}>
                              <Btn v="primary" sz="sm" disabled={!row.__approvedContractId || running || extractAllRunning !== null} onClick={() => runExtractAll(row.__id)}>
                                {running && prog ? `Extracting ${prog.current}/${prog.total}: ${prog.currentLabel}` : `Extract ${pendingCount} Template${pendingCount === 1 ? "" : "s"}`}
                              </Btn>
                            </span>
                            {running && <span style={{ fontSize: 10, color: T.txM }}>Serial — each template processed sequentially</span>}
                          </>
                        )}
                      </div>
                    );
                  })()}
                  {row.__detectedExhibits.map((exhibit: any, idx: number) => {
                    const exhKey = `${row.__id}:${idx}`;
                    const isExtracting = extractingExhibit === exhKey;
                    const isExtracted = exhibit.status === "extracted";
                    const errMsg = exhibitErrors[exhKey];
                    return (
                      <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: idx > 0 ? `1px solid rgba(26,138,158,.1)` : "none" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, color: T.tx, fontWeight: 500 }}>{exhibit.title || exhibit.type}</div>
                          <div style={{ fontSize: 10, color: T.txD, fontFamily: "monospace" }}>{exhibit.type}</div>
                          {errMsg && <div style={{ fontSize: 10, color: T.red, marginTop: 2 }}>{errMsg}</div>}
                        </div>
                        {isExtracted ? (
                          <Badge c={T.grn} b={T.grnS}>✓ Extracted</Badge>
                        ) : isExtracting ? (
                          <Badge c={T.ylw} b={T.ylwS}>Processing…</Badge>
                        ) : (
                          <Badge c={T.txD} b={T.bg3}>Pending</Badge>
                        )}
                      </div>
                    );
                  })}
                  {row.__approved && (
                    <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
                      <Btn v="ghost" sz="sm" onClick={() => {
                        locallyActedIdsRef.current.add(row.__id);
                        // If this is the last remaining row, reset the entire AI Import
                        // state so the screen returns to a clean, empty batch. Otherwise
                        // just drop this row and let the remaining rows keep working.
                        if (results.length <= 1) {
                          setBatchId(null);
                          setBatchJobs([]);
                          setResults([]);
                          setFiles([]);
                          setRealFiles([]);
                          setImportedCount(0);
                          setBatchTotal(0);
                          setBatchCompleted(0);
                          setShowResumeBanner(false);
                          setShowNavSafe(false);
                          setError("");
                          locallyActedIdsRef.current.clear();
                        } else {
                          setResults(prev => prev.filter((r: any) => r.__id !== row.__id));
                        }
                      }}>Done with this contract</Btn>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );})}
          {!processing && importedCount > 0 && <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.brd}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: T.txM }}>{importedCount} contract{importedCount !== 1 ? "s" : ""} imported{results.length > 0 ? `, ${results.length} remaining` : ""}</span>
            {results.length === 0 && <Btn v="primary" sz="sm" onClick={() => { setBatchId(null); setBatchJobs([]); setResults([]); setFiles([]); setRealFiles([]); setImportedCount(0); setBatchTotal(0); setBatchCompleted(0); setShowResumeBanner(false); setShowNavSafe(false); }}>Start New Batch</Btn>}
          </div>}
        </Card>
      )}
      {pdfHighlight && <PdfHighlightModal file={pdfHighlight.file} searchText={pdfHighlight.searchText} sectionRef={pdfHighlight.sectionRef} base64={pdfHighlight.base64} fieldName={pdfHighlight.fieldName} fieldValue={pdfHighlight.fieldValue} onClose={() => setPdfHighlight(null)} />}

      {/* Replace-existing-template confirm modal */}
      <Modal open={!!replaceConfirm} onClose={() => { replaceResolveRef.current?.("skip"); setReplaceConfirm(null); replaceResolveRef.current = null; }} title="Replace Existing Template?" w={560}>
        {replaceConfirm && <div style={{ padding: 4 }}>
          <p style={{ fontSize: 12, color: T.tx, marginTop: 0 }}>A template of type <strong>{replaceConfirm.exhibitTitle}</strong> is already linked to this contract:</p>
          <div style={{ padding: "10px 12px", backgroundColor: T.bg3, borderRadius: 4, marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: T.tx, fontWeight: 600 }}>{replaceConfirm.existing_template_name}</div>
            <div style={{ fontSize: 11, color: T.txM }}>Current version: v{replaceConfirm.existing_version}</div>
          </div>
          <p style={{ fontSize: 11, color: T.txM, lineHeight: 1.5 }}>Replacing will save a new <strong>v{replaceConfirm.next_version}</strong> alongside the existing version in the template's history. The existing file stays in version history; the latest version becomes the active template. Nothing is deleted.</p>
          <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
            <Btn v="ghost" sz="sm" onClick={() => { replaceResolveRef.current?.("skip"); setReplaceConfirm(null); replaceResolveRef.current = null; }}>Skip this template</Btn>
            <Btn v="primary" sz="sm" onClick={() => { replaceResolveRef.current?.("replace"); }}>Replace — save as v{replaceConfirm.next_version}</Btn>
          </div>
        </div>}
      </Modal>

      {/* Per-template wizard modal: placeholder mapping + SharePoint save */}
      <TemplateWizardModal
        data={templateWizard}
        scanningTemplateId={scanningTemplateId}
        setScanningTemplateId={setScanningTemplateId}
        onUpdatePlaceholders={(next: any[]) => setTemplateWizard((cur: any) => cur ? { ...cur, placeholder_fields: next } : cur)}
        onClose={() => { wizardResolveRef.current?.(); setTemplateWizard(null); wizardResolveRef.current = null; }}
      />
    </div>
  );
};

// Modal component: placeholder mapping review + SharePoint save for a single
// template produced by the exhibit extraction flow. Rendered once at the
// AIImportView root and driven by `data` (null = closed).
const TemplateWizardModal = ({ data, scanningTemplateId, setScanningTemplateId, onUpdatePlaceholders, onClose }: {
  data: any; scanningTemplateId: string | null; setScanningTemplateId: (v: string | null) => void; onUpdatePlaceholders: (p: any[]) => void; onClose: () => void;
}) => {
  const [syncStatus, setSyncStatus] = useState<{ kind: "idle" | "saving" | "synced" | "local" | "error"; message?: string }>({ kind: "idle" });
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (data) { setSyncStatus({ kind: "idle" }); setEditing(false); }
  }, [data?.template_id]);

  if (!data) return <Modal open={false} onClose={onClose} title="" w={700}>{null}</Modal>;

  const placeholders = Array.isArray(data.placeholder_fields) ? data.placeholder_fields : [];

  const runAIScan = async () => {
    setScanningTemplateId(data.template_id);
    try {
      const r = await apiCall(`/self-service/templates/${data.template_id}/scan-placeholders`, { method: "POST" });
      if (r?.placeholder_fields) onUpdatePlaceholders(r.placeholder_fields);
    } finally {
      setScanningTemplateId(null);
    }
  };

  const savePlaceholders = async () => {
    await apiCall(`/self-service/templates/${data.template_id}`, { method: "PUT", body: JSON.stringify({ placeholder_fields: placeholders }) });
  };

  const saveAndSync = async () => {
    setSyncStatus({ kind: "saving" });
    try {
      await savePlaceholders();
      const r = await apiCall(`/extraction/templates/${data.template_id}/sync-to-contract-folder`, { method: "POST" });
      if (r?.synced) {
        setSyncStatus({ kind: "synced", message: r.sharepoint_folder });
      } else if (r?.local_only) {
        setSyncStatus({ kind: "local", message: r.reason || "SharePoint not available — saved locally" });
      } else {
        setSyncStatus({ kind: "error", message: r?.error || "Sync failed" });
      }
    } catch (err: any) {
      setSyncStatus({ kind: "error", message: err.message });
    }
  };

  const saveLocalOnly = async () => {
    setSyncStatus({ kind: "saving" });
    try {
      await savePlaceholders();
      setSyncStatus({ kind: "local", message: "Saved locally (SharePoint sync skipped)" });
    } catch (err: any) {
      setSyncStatus({ kind: "error", message: err.message });
    }
  };

  const updateField = (i: number, patch: any) => {
    const next = [...placeholders];
    next[i] = { ...next[i], ...patch };
    onUpdatePlaceholders(next);
  };
  const removeField = (i: number) => onUpdatePlaceholders(placeholders.filter((_: any, j: number) => j !== i));
  const addField = () => onUpdatePlaceholders([...placeholders, { field_key: "", label: "", source: "user_input", auto_fill: false }]);

  return (
    <Modal open={true} onClose={syncStatus.kind === "saving" ? undefined : onClose} title={`Configure Template: ${data.template_name}`} w={860}>
      <div style={{ padding: 4 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 11, color: T.txM }}>Exhibit: <span style={{ color: T.tx, fontWeight: 600 }}>{data.exhibitTitle}</span></div>
          <div style={{ fontSize: 11, color: T.txM }}>File: <span style={{ fontFamily: "monospace", color: T.tx }}>{data.file_name}</span></div>
          {data.replaced && <Badge c={T.orn} b={T.ornS}>Replaced → v{data.version}</Badge>}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: T.tx, fontWeight: 700 }}>Step 1 — Placeholder Fields ({placeholders.length})</div>
          <div style={{ display: "flex", gap: 6 }}>
            <Btn v="ghost" sz="sm" disabled={scanningTemplateId === data.template_id} onClick={runAIScan}>{scanningTemplateId === data.template_id ? "Scanning…" : "AI Re-scan"}</Btn>
            <Btn v="ghost" sz="sm" onClick={() => setEditing(!editing)}>{editing ? "Done editing" : "Edit"}</Btn>
          </div>
        </div>

        <div style={{ border: `1px solid ${T.brd}`, borderRadius: 4, maxHeight: 300, overflowY: "auto", marginBottom: 14 }}>
          {placeholders.length === 0 && <div style={{ padding: 12, fontSize: 11, color: T.txD }}>No placeholder fields. Click AI Re-scan to detect.</div>}
          {placeholders.map((p: any, i: number) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr 140px 36px", gap: 8, alignItems: "center", padding: "6px 10px", borderBottom: i < placeholders.length - 1 ? `1px solid ${T.brd}` : "none" }}>
              {editing ? (
                <>
                  <input value={p.field_key || ""} onChange={e => updateField(i, { field_key: e.target.value })} placeholder="FIELD_KEY" style={{ fontSize: 11, padding: "4px 8px", fontFamily: "monospace", border: `1px solid ${T.brd}`, borderRadius: 3, backgroundColor: T.bg3, color: T.tx }} />
                  <input value={p.label || ""} onChange={e => updateField(i, { label: e.target.value })} placeholder="Human label" style={{ fontSize: 11, padding: "4px 8px", border: `1px solid ${T.brd}`, borderRadius: 3, backgroundColor: T.bg3, color: T.tx }} />
                  <select value={p.source || "user_input"} onChange={e => updateField(i, { source: e.target.value, auto_fill: e.target.value === "parent_metadata" })} style={{ fontSize: 11, padding: "4px 8px", border: `1px solid ${T.brd}`, borderRadius: 3, backgroundColor: T.bg3, color: T.tx }}>
                    <option value="user_input">User input</option>
                    <option value="parent_metadata">From parent</option>
                  </select>
                  <Btn v="ghost" sz="sm" onClick={() => removeField(i)}>✕</Btn>
                </>
              ) : (
                <>
                  <span style={{ fontSize: 11, fontFamily: "monospace", color: T.tx }}>{`{{${p.field_key}}}`}</span>
                  <span style={{ fontSize: 11, color: T.txM }}>{p.label || "(no label)"}</span>
                  <Badge c={p.source === "parent_metadata" ? T.grn : T.txD} b={p.source === "parent_metadata" ? T.grnS : T.bg3}>{p.source === "parent_metadata" ? "Auto" : "User input"}</Badge>
                  <span />
                </>
              )}
            </div>
          ))}
          {editing && <div style={{ padding: "6px 10px", borderTop: `1px solid ${T.brd}` }}><Btn v="ghost" sz="sm" onClick={addField}>+ Add placeholder</Btn></div>}
        </div>

        <div style={{ fontSize: 12, color: T.tx, fontWeight: 700, marginBottom: 8 }}>Step 2 — Save Location</div>
        <div style={{ fontSize: 11, color: T.txM, marginBottom: 10, lineHeight: 1.5 }}>
          Save to SharePoint uploads the template to the parent contract's Repository folder under a new <code style={{ backgroundColor: T.bg3, padding: "1px 4px", borderRadius: 3 }}>Templates/</code> subfolder. If SharePoint is not configured for this tenant or the parent contract was not itself synced, it falls back to local storage automatically.
        </div>

        {syncStatus.kind === "saving" && <div style={{ padding: "8px 12px", backgroundColor: T.ylwS, color: T.ylw, fontSize: 11, borderRadius: 4, marginBottom: 10 }}>Saving…</div>}
        {syncStatus.kind === "synced" && <div style={{ padding: "8px 12px", backgroundColor: T.grnS, color: T.grn, fontSize: 11, borderRadius: 4, marginBottom: 10 }}>✓ Synced to SharePoint{syncStatus.message ? ` (${syncStatus.message})` : ""}</div>}
        {syncStatus.kind === "local" && <div style={{ padding: "8px 12px", backgroundColor: T.cynS, color: T.cyn, fontSize: 11, borderRadius: 4, marginBottom: 10 }}>Saved locally: {syncStatus.message}</div>}
        {syncStatus.kind === "error" && <div style={{ padding: "8px 12px", backgroundColor: T.redS, color: T.red, fontSize: 11, borderRadius: 4, marginBottom: 10 }}>Error: {syncStatus.message}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          {syncStatus.kind === "idle" || syncStatus.kind === "error" ? (
            <>
              <Btn v="ghost" sz="sm" onClick={saveLocalOnly}>Save Local Only</Btn>
              <Btn v="primary" sz="sm" onClick={saveAndSync}>Save &amp; Sync to SharePoint</Btn>
            </>
          ) : syncStatus.kind === "saving" ? null : (
            <Btn v="primary" sz="sm" onClick={onClose}>{syncStatus.kind === "synced" || syncStatus.kind === "local" ? "Next Template →" : "Close"}</Btn>
          )}
        </div>
      </div>
    </Modal>
  );
};
